import React from 'react';
import { Employee, WeeklySchedule, EmployeeStatusEnum, TimeSlot } from '../types';
import { StatusCell } from './StatusCell';
import { getCurrentWeekDates } from '../mockData';
import { calculateWeeklyHours, formatHours } from '../utils';

interface PlanningTableProps {
    employees: Employee[];
    schedules: WeeklySchedule[];
    weekStartDate: Date;
    onScheduleUpdate?: (employeeId: string, date: string, status: EmployeeStatusEnum, timeSlots: TimeSlot[]) => void;
}

export const PlanningTable: React.FC<PlanningTableProps> = ({
    employees,
    schedules,
    weekStartDate,
    onScheduleUpdate
}) => {
    const weekDates = getCurrentWeekDates(weekStartDate);
    const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    
    const today = new Date();
    const todayKey = today.toISOString().split('T')[0];

    const getEmployeeSchedule = (employeeId: string) => {
        return schedules.find(schedule => schedule.employeeId === employeeId);
    };

    const isToday = (date: Date) => {
        const dateKey = date.toISOString().split('T')[0];
        return dateKey === todayKey;
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="sticky left-0 bg-gray-50 px-6 py-4 text-left text-sm font-semibold text-gray-900 border-r border-gray-200 min-w-[200px]">
                                Employé
                            </th>
                            {weekDates.map((date, index) => (
                                <th
                                    key={date.toISOString()}
                                    className={`px-4 py-4 text-center text-sm font-semibold min-w-[120px]
                                        ${isToday(date) 
                                            ? 'text-blue-700 bg-blue-50' 
                                            : 'text-gray-900'
                                        }
                                        ${index === 5 || index === 6 ? 'bg-gray-100' : ''}
                                    `}
                                >
                                    <div>{dayNames[index]}</div>
                                    <div className="text-xs font-normal mt-1 text-gray-600">
                                        {formatDate(date)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {employees.map((employee, employeeIndex) => {
                            const employeeSchedule = getEmployeeSchedule(employee.id);
                            
                            return (
                                <tr 
                                    key={employee.id} 
                                    className={employeeIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}
                                >
                                    <td className="sticky left-0 px-6 py-4 border-r border-gray-200 bg-inherit">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                                                    <span className="text-sm font-medium text-white">
                                                        {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="ml-3">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {employee.firstName} {employee.lastName}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {employee.position}
                                                </div>
                                                {/* Weekly hours total */}
                                                <div className="text-xs text-blue-600 font-medium mt-1">
                                                    {formatHours(calculateWeeklyHours(employeeSchedule || { employeeId: employee.id, weekStartDate, days: {} }))} / semaine
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    {weekDates.map((date) => {
                                        const dateKey = date.toISOString().split('T')[0];
                                        const daySchedule = employeeSchedule?.days[dateKey];
                                        const status = daySchedule?.status || EmployeeStatusEnum.DEFAULT;
                                        const timeSlots = daySchedule?.timeSlots || [];
                                        
                                        return (
                                            <td key={dateKey} className="px-2 py-2">
                                                <StatusCell
                                                    status={status}
                                                    timeSlots={timeSlots}
                                                    isToday={isToday(date)}
                                                    onClick={() => {
                                                        console.log('Cell clicked:', employee.id, dateKey);
                                                    }}
                                                    onStatusChange={(newStatus, newTimeSlots) => {
                                                        if (onScheduleUpdate) {
                                                            onScheduleUpdate(employee.id, dateKey, newStatus, newTimeSlots);
                                                        }
                                                    }}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};